customElements.whenDefined('search-form').then(() => {
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.productTypes = this.querySelector('.js-product-types');
      this.init();
    }

    init() {
      this.setupEventListeners();
      this.updateQueryState();
      this.closest('details')?.addEventListener('toggle', () => {
        if (!this.closest('details').open) this.close();
      });
      if (this.input.matches(':focus') && this.getQuery()) this.onChange();
    }

    setupEventListeners() {
      if (this.resetButton) this.resetButton.hidden = !this.getQuery();
      this.addEventListener('focusin', event => {
        if (event.target === this.input) return;
        this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
        this.input.removeAttribute('aria-activedescendant');
      });
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('input', () => {
        this.updateQueryState();
        this.abortController.abort();
        this.requestVersion = (this.requestVersion || 0) + 1;
        this.close();
        if (this.resetButton) this.resetButton.hidden = !this.getQuery();
        clearTimeout(this.searchTimer);
        if (!this.input.matches(':focus')) return;
        this.searchTimer = setTimeout(() => this.onChange(), 250);
      });
      this.productTypes?.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.closest('.template-search__search') || this.closest('.header__search-bar')) {
        this.addEventListener('focusout', this.onFocusOut.bind(this));
      }

      this.addEventListener('click', (event) => {
        if (event.target.matches('.inline-search__close-button') || event.target.closest('.inline-search__close-button')) {
          this.onFormReset();
          this.close();
          document.activeElement.blur();
          setTimeout(() => { document.activeElement.blur(); }, 500);
        }
      });
    }

    updateQueryState() {
      this.toggleAttribute('has-query', Boolean(this.getQuery()));
      const error = this.querySelector('.evolt-search-error');
      if (error) error.hidden = true;
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.classList.add('opacity-20');
      }

      if(newSearchTerm.length === 0){
        // Remove the results when they are no longer relevant for the new search term
        // so they don't show up when the dropdown opens again
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    onFormReset(event) {
      event?.preventDefault();
      clearTimeout(this.searchTimer);
      this.abortController.abort();
      this.requestVersion = (this.requestVersion || 0) + 1;
      this.input.value = '';
      this.updateQueryState();
      this.searchTerm = '';
      if (this.resetButton) this.resetButton.hidden = true;
      this.close(true);
      this.input.focus();
    }

    onFocus() {
      this.updateQueryState();
      const currentSearchTerm = this.getQuery();

      if (window.visualViewport) {
        setTimeout(() => {
          document.documentElement.style.setProperty('--bvh', `${window.visualViewport.height}px`);
        }, 100);

        setTimeout(() => {
          document.documentElement.style.setProperty('--bvh', `${window.visualViewport.height}px`);
        }, 500);
      }

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from other search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.close();
        }
      });
    }

    onKeyup(event) {
      if (event.code === 'Escape') { this.close(); this.input.blur(); return; }
      if (!this.getQuery().length) this.close(true);
      if (event.target !== this.input || !['ArrowUp', 'ArrowDown', 'Enter'].includes(event.code)) return;
      event.preventDefault();

      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (event.target === this.input && (event.code === 'ArrowUp' || event.code === 'ArrowDown')) {
        event.preventDefault();
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');

      // Filter out hidden elements (duplicated page and article resources) thanks
      // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
      const allVisibleElements = Array.from(this.querySelectorAll('[role="option"], button.predictive-search__item')).filter(
          (element) => element.offsetParent !== null
      );
      if (!allVisibleElements.length) return;
      let activeElementIndex = 0;

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }

      this.statusElement.textContent = '';

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute('aria-selected', true);
      activeElement.scrollIntoView({ block: 'nearest' });
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

      if (selectedOption) selectedOption.click();
    }

    getSearchResults(searchTerm) {
      this.abortController.abort();
      this.abortController = new AbortController();
      const version = this.requestVersion = (this.requestVersion || 0) + 1;
      const formData = new FormData(this.previousElementSibling?.matches('.predictive_search_form') ? this.previousElementSibling : document.querySelector('.predictive_search_page_form') || document.querySelector('.predictive_search_form'));
      const params = new URLSearchParams(formData);

      if (this.productTypes && this.productTypes.value.length > 0) {
        searchTerm = `product_type:${this.productTypes.value} AND ${searchTerm}`;
      }

      params.set('q', searchTerm);

      const urlParams = params.toString();
      const queryKey = urlParams.toLowerCase();

      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      fetch(`${this.dataset?.searchUrl || routes.predictive_search_url}?${urlParams}`, {
        signal: this.abortController.signal,
      })
          .then((response) => {
            if (!response.ok) {
              var error = new Error(response.status);
              throw error;
            }

            return response.text();
          })
          .then((text) => {
            if (version !== this.requestVersion || this.getQuery() !== this.searchTerm) return;
            const resultsMarkup = new DOMParser()
                .parseFromString(text, 'text/html')
                .querySelector('#shopify-section-predictive-search').innerHTML;
            // Save bandwidth keeping the cache in all instances synced
            this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
              const cache = predictiveSearchInstance.cachedResults;
              if (Object.keys(cache).length >= 20) delete cache[Object.keys(cache)[0]];
              cache[queryKey] = resultsMarkup;
            });
            this.renderSearchResults(resultsMarkup);
          })
          .catch((error) => {
            if (error?.code === 20) {
              // Code 20 means the call was aborted
              return;
            }
            if (version === this.requestVersion) {
              this.close();
              const errorMessage = this.querySelector('.evolt-search-error');
              if (errorMessage) errorMessage.hidden = false;
            }
          });
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.querySelector('.search-suggestions')?.classList.add('hidden');

      this.predictiveSearchResults.innerHTML = resultsMarkup;
      const prefix = this.input.id || 'search';
      this.predictiveSearchResults.querySelectorAll('[id]').forEach(node => {
        node.id = `${prefix}-${node.id}`;
      });
      this.predictiveSearchResults.querySelectorAll('[aria-labelledby]').forEach(node => {
        node.setAttribute('aria-labelledby', node.getAttribute('aria-labelledby').split(' ').map(id => `${prefix}-${id}`).join(' '));
      });
      const list = this.predictiveSearchResults.querySelector('[role="listbox"]');
      if (list) this.input.setAttribute('aria-controls', list.id);
      this.predictiveSearchResults.querySelectorAll('a[tabindex="-1"]').forEach(link => link.removeAttribute('tabindex'));
      this.setAttribute('results', true);

      this.setLiveRegionResults();
      this.open();
      if (window.updateViewportSize) window.updateViewportSize();
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
    }

    open() {
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      clearTimeout(this.searchTimer);
      this.abortController.abort();
      this.requestVersion = (this.requestVersion || 0) + 1;
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
      }
      this.querySelector('.search-suggestions')?.classList.remove('hidden');
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.removeAttribute('results');
      this.input.setAttribute('aria-expanded', false);
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
});